import React, { Component } from 'react';
import { connect } from 'react-redux';
import { heatSelect } from '~/store/actions/heat';
import { isLiveChanged } from '~/store/actions/is-live';
import PropTypes from 'prop-types';
import { popup, translate } from '~/decorators';
import Scrollable from '~/views/Home/base/Scrollable';
import SizeInformer from '~/views/Home/base/SizeInformer';
import TabSwitcher from '~/views/Home/base/TabSwitcher';
import oget from 'oget';
import isEqual from 'lodash.isequal';

import './index.scss';
import '../table/TableLeaderboard/index.scss';
import isLeaderboardWithDetail from '~/utils/isLeaderboardWithDetail';
import isLeaderboardWithTotal from '~/utils/isLeaderboardWithTotal';
import DuelCompetitor from './DuelCompetitor';

export default
@popup
@translate('home.table.duel')
@connect(null, (dispatch) => ({
  onHeatSelect: (...args) => dispatch(heatSelect(...args)),
  onIsLiveChanged: (...args) => dispatch(isLiveChanged(...args)),
}))
class Duel extends Component {
  state = {
    activeExternalAthleteId: null,
    scrollToTop: false,
    scrollToLive: false,
    hasScrollbar: false,
    roundConfig: {},
    leaderboard: [],
    tabsHeight: 0,
    fullScreenMode: false,
  };

  static propTypes = {
    round: PropTypes.oneOfType([(item) => item === null, PropTypes.object.isRequired]),
    contest: PropTypes.oneOfType([(item) => item === null, PropTypes.object.isRequired]),
    event: PropTypes.shape({
      startListColumns: PropTypes.object,
      leaderboardColumns: PropTypes.object,
      leaderboardDetails: PropTypes.object,
      eventId: PropTypes.string,
    }).isRequired,
    heats: PropTypes.array.isRequired,
    heatsById: PropTypes.object.isRequired,
    heatId: PropTypes.any,
    roundState: PropTypes.any,
    athletes: PropTypes.shape({
      expectedHeatId: PropTypes.any,
      byId: PropTypes.object,
      isLiveId: PropTypes.any,
    }).isRequired,
    isLive: PropTypes.shape({
      athleteId: PropTypes.any,
      heatId: PropTypes.any,
      changed: PropTypes.bool,
    }).isRequired,
    colors: PropTypes.shape({
      backgroundColor: PropTypes.string,
      color: PropTypes.string,
      itemOdd: PropTypes.shape({
        backgroundColor: PropTypes.string,
        borderColor: PropTypes.string,
      }),
      itemEven: PropTypes.shape({
        backgroundColor: PropTypes.string,
        borderColor: PropTypes.string,
      }),
      itemActive: PropTypes.shape({
        background: PropTypes.string,
        borderColor: PropTypes.string,
        color: PropTypes.string,
      }),
      itemNext: PropTypes.shape({
        borderColor: PropTypes.string,
      }),
      scores: PropTypes.shape({
        default: PropTypes.shape({
          color: PropTypes.string,
        }),
        active: PropTypes.shape({
          color: PropTypes.string,
        }),
        highlight: PropTypes.shape({
          color: PropTypes.string,
        }),
        highlightActive: PropTypes.shape({
          color: PropTypes.string,
        }),
        extra: PropTypes.shape({
          color: PropTypes.string,
        }),
        extraActive: PropTypes.shape({
          color: PropTypes.string,
        }),
        drop: PropTypes.shape({
          color: PropTypes.string,
        }),
      }),
    }).isRequired,
    deviceType: PropTypes.string.isRequired,
  };

  static getDerivedStateFromProps(
    { isLive, heatsById, heatId, round, event },
    { activeExternalAthleteId: prevActiveExternalAthleteId, leaderboard: prevLeaderboard },
  ) {
    const leaderboard = oget(heatsById, `[${heatId}].results`, []);

    const { hasTricks, labelsCount, ...info } = oget(heatsById, `[${heatId}].info`, {});

    const mostRunsCount = hasTricks ? (info.mostTricksCount > 5 ? 1 : 2) : info.mostRunsCount;

    const roundConfig = {
      cut: oget(round, 'cut'),
      showTotal: isLeaderboardWithTotal(leaderboard),
      showNeeds: oget(round, 'showneeds', oget(round, 'showNeeds')) !== false,
      showRuns: isLeaderboardWithDetail(leaderboard, 'Runs'),
      autoFocus: oget(round, 'autofocus', oget(round, 'autoFocus')) !== false,
      labelsCount,
      mostRunsCount: mostRunsCount > 10 ? 10 : mostRunsCount,
      mostTricksCount: hasTricks ? (mostRunsCount === 1 ? 6 : 5) : 0,
      mostJudgesCount: 5,
      showJudgeScores: event.hasOwnProperty('leaderboardDetails') ? !!event.leaderboardDetails['Judges Scores'] : false,
      testData: oget(heatsById, `[${heatId}].testData`, ''),
    };

    const activeExternalAthleteId = isLive.athleteId;

    if (!isEqual(prevLeaderboard, leaderboard)) {
      return {
        leaderboard,
        roundConfig,
        scrollToTop: true,
        scrollToLive: true,
      };
    }

    if (!activeExternalAthleteId) {
      return {
        leaderboard,
        roundConfig,
      };
    }

    if (activeExternalAthleteId === prevActiveExternalAthleteId) {
      return {
        leaderboard,
        roundConfig,
      };
    }

    return {
      leaderboard,
      roundConfig,
      activeExternalAthleteId,
      scrollToLive: true,
    };
  }

  removeContentRefs = () => {
    this.scrollDiv = null;
    this.isLiveDiv = null;
  };

  componentWillUnmount() {
    this.removeContentRefs();
    this.containerDiv = null;
  }

  componentDidUpdate() {
    const {
      scrollToLive,
      scrollToTop,
      roundConfig: { autoFocus },
    } = this.state;

    if (!this.scrollDiv) {
      return;
    }

    if (!autoFocus && scrollToTop) {
      this.scrollDiv.scrollTop = 0;

      this.setState({
        scrollToTop: false,
      });
    }

    if (autoFocus && scrollToLive) {
      this.scrollDiv.scrollTop = this.isLiveDiv ? this.isLiveDiv.offsetTop : 0;

      this.setState({
        scrollToLive: false,
      });
    }

    const { isLive, heatsById, onIsLiveChanged, onHeatSelect } = this.props;

    if (!isLive.changed && isLive.heatId) {
      onIsLiveChanged();

      onHeatSelect(heatsById[isLive.heatId]);
    }
  }

  lastResizeDate = new Date();
  lastWidth = 0;

  listWidth = () => {
    if (!this.containerDiv) {
      return 0;
    }

    const style = window.getComputedStyle(this.containerDiv.parentNode);

    const width = parseInt(style.width, 10);

    if (width !== this.lastWidth) {
      this.lastWidth = width;
      this.lastResizeDate = new Date();
    }

    return width;
  };

  className = () => {
    if (!this.containerDiv) {
      return '';
    }

    return `width-${this.listWidth()}`;
  };

  onScrollableUpdate = ({ hasScrollbar }) =>
    this.setState({
      hasScrollbar,
    });

  toggleFullscreenMode = () => {
    this.props.toggleFullscreenMode();
  };

  render() {
    const {
      t,
      athletes,
      heats,
      heatId,
      onHeatSelect,
      colors,
      round,
      popupHide,
      fullscreenMode,
      fullscreenModeForiFrame,
      event,
    } = this.props;

    const { leaderboard, hasScrollbar, tabsHeight } = this.state;

    const roundType = oget(round, 'type');
    const fullscreenIcon = {
      color: colors.itemActive.backgroundColor,
    };
    const hasTabs = heats.length > 1;
    const fullscreenClass = fullscreenMode
      ? 'icon-size-actual icons fullscreen-exit'
      : 'icon-size-fullscreen icons fullscreen';
    return (
      <div
        ref={(ref) => {
          this.containerDiv = ref;

          if (!leaderboard.length) {
            this.removeContentRefs();
          }
        }}
        className={[
          'table-leaderboard',
          hasScrollbar ? 'has-scrollbar' : '',
          hasTabs ? 'has-tabs' : '',
          this.className(),
        ].join(' ')}
      >
        {!fullscreenModeForiFrame && (
          <i className={fullscreenClass} style={fullscreenIcon} onClick={this.toggleFullscreenMode} />
        )}
        <div className="table-title">
          {t('Leaderboard')}
          {round && round.info ? <span className="round-info">{round.info}</span> : null}
        </div>

        {hasTabs && (
          <SizeInformer
            lastUpdate={this.lastResizeDate}
            onResized={({ height }) => {
              this.setState({
                tabsHeight: height,
              });
            }}
          >
            <TabSwitcher
              tabs={heats}
              renderTab={(heat, i) => {
                let classActive = '';
                let style = {};
                if (heat.externalId === heatId) {
                  classActive = 'active';
                  style = {
                    backgroundColor: colors.itemActive.backgroundColor,
                  };
                }
                return (
                  <span
                    key={`startlist_heats_tabs_${i}`}
                    className={classActive}
                    style={style}
                    onClick={() => onHeatSelect(heat)}
                  >
                    {heat.name}
                  </span>
                );
              }}
            />
          </SizeInformer>
        )}

        {!leaderboard.length && <div className="no-info">{t('No live information at this time')}</div>}

        {leaderboard.length > 0 && (
          <Scrollable
            onUpdate={this.onScrollableUpdate}
            onScroll={popupHide}
            onContentRef={(ref) => (this.scrollDiv = ref)}
            style={hasTabs ? { top: tabsHeight + 47 } : {}}
          >
            {leaderboard.map((group, i) => {
              group.sort((a, b) => a.rank - b.rank);
              return (
                <div key={`duel_${i}`} className="duel-grid">
                  <div className="spacer"></div>
                  {group
                    .filter((result) => result.rank || result.value)
                    .map((result, resultIndex) => {
                      const athlete = athletes.byId[result.externalAthleteId] || {};
                      return (
                        <DuelCompetitor
                          key={`group_${i}_duel_competitor_${result.externalAthleteId}`}
                          className="competitor"
                          groupIndex={i}
                          isHighestRank={resultIndex === 0}
                          roundType={roundType}
                          colors={colors.scores}
                          athlete={athlete}
                          result={result}
                          eventId={event.eventId}
                        />
                      );
                    })}
                  <div className="spacer"></div>
                </div>
              );
            })}
          </Scrollable>
        )}
      </div>
    );
  }
}
